import AttachFileIcon from "@mui/icons-material/AttachFile";
import CommentIcon from "@mui/icons-material/Comment";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import MailIcon from "@mui/icons-material/Mail";
import MergeIcon from "@mui/icons-material/Merge";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useParams } from "react-router-dom";
import { ROLE_USER, TICKETS } from "../../Constants/RolesConstants";
import Activity from "./Activity";
import AI from "./AI";
import Comment from "./Comment";
import EditTicket from "./EditTicket";
import Email from "./Email";
import MergeTickets from "./MergeTickets";
import MultipleFileUploader from "./MultipleFileUploader";
import Solution from "./Solution";
import { useTheme } from "@mui/material";
import AxiosInstance from '../../services/AxiosInstance'
import { TICKET_URL } from '../../Constants/Constants'

function TicketTab(props) {
  const [isLoading, setLoading] = useState(true);
  let { viewTicketId } = useParams();
  const [statusId, setStatusId] = useState('');

  const getTicketInfo = async () => {
    await AxiosInstance.get(`${TICKET_URL}/${viewTicketId}`).then((response) => {
      setStatusId(response.data?.statusId);
    });
  }
  useEffect(() => {
    getTicketInfo();
    setLoading(false);
  }, []);

  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  const theme = useTheme();

  // Tab configuration array
  const tabConfig = [
    {
      label: "Summary",
      value: 0,
      icon: <InfoIcon sx={{ fontSize: 18 }} />,
      tooltip: "Ticket Summary",
      panel: <EditTicket componentPermissions={props.permissions[TICKETS]} />,
      show: true,
    },
    {
      label: "Comments",
      value: 1,
      icon: <CommentIcon sx={{ fontSize: 18 }} />,
      tooltip: "View and add comments",
      panel: (
        <Row>
          <Comment
            ticketId={viewTicketId}
            statusId={statusId}
            componentPermissions={props.permissions[TICKETS]}
          />
        </Row>
      ),
      show: true,
    },
    {
      label: "Attachments",
      value: 2,
      icon: <AttachFileIcon sx={{ fontSize: 18 }} />,
      tooltip: "View and upload attachments",
      panel: (
        <Row>
          <Form.Group controlId="formFileMultiple">
            <MultipleFileUploader
              ticketId={viewTicketId}
              statusId={statusId}
              componentPermissions={props.permissions[TICKETS]}
            />
          </Form.Group>
        </Row>
      ),
      show: true,
    },
    {
      label: "Emails",
      value: 3,
      icon: <MailIcon sx={{ fontSize: 18 }} />,
      tooltip: "View ticket emails",
      panel: <Email statusId={statusId} componentPermissions={props.permissions[TICKETS]} />,
      show: true,
    },
    {
      label: "Activity Info",
      value: 4,
      icon: <HistoryIcon sx={{ fontSize: 18 }} />,
      tooltip: "Ticket activity log",
      panel: <Activity ticketId={viewTicketId} />,
      show: true,
    },
    {
      label: "Solutions",
      value: 5,
      icon: <LightbulbIcon sx={{ fontSize: 18 }} />,
      tooltip: "Suggested solutions",
      panel: (
        <Solution
          ticketId={viewTicketId}
          statusId={statusId}
          componentPermissions={props.permissions[TICKETS]}
        />
      ),
      show: props.userRole !== ROLE_USER,
    },
    {
      label: "Merged Tickets",
      value: 6,
      icon: <MergeIcon sx={{ fontSize: 18 }} />,
      tooltip: "View merged tickets",
      panel: <MergeTickets ticketId={viewTicketId} statusId={statusId} />,
      show: props.userRole !== ROLE_USER,
    },
    {
      label: "AI",
      value: 7,
      icon: <SettingsSuggestIcon sx={{ fontSize: 18 }} />,
      tooltip: "AI suggestions",
      panel: (
        <AI
          ticketId={viewTicketId}
          statusId={statusId}
          componentPermissions={props.permissions[TICKETS]}
        />
      ),
      show: props.userRole !== ROLE_USER,
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        width: "100%",
        typography: "body1",
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <TabContext value={value}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        >
          <TabList
            onChange={handleChange}
            aria-label="Ticket details tabs"
            variant="scrollable"
            textColor="secondary"
            indicatorColor="#000"
            scrollButtons={false}
            sx={{
              minHeight: 48,
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              ".MuiTab-root": {
                color: isDarkMode ? theme.palette.common.black : theme.palette.common.white,
                minHeight: 48,
                fontWeight: 500,
                letterSpacing: 0.5,
                px: 2.5,
                py: 1.2,
                margin: 0,
                borderRadius: 8,
                transition: "background 0.2s, color 0.2s, box-shadow 0.2s",
              },
              ".Mui-selected": {
                color: isDarkMode ? theme.palette.common.black : theme.palette.common.white,
              },
              ".MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            {tabConfig
              .filter((tab) => tab.show)
              .map((tab) => (
                <Tooltip key={tab.value} title={tab.tooltip} arrow>
                  <Tab
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                    value={tab.value}
                    aria-label={tab.label}
                    aria-selected={value === tab.value}
                  />
                </Tooltip>
              ))}
          </TabList>
        </Box>

        {tabConfig
          .filter((tab) => tab.show)
          .map((tab) => (
            <TabPanel
              key={tab.value}
              value={tab.value}
              sx={{
                background: "#fff",
                borderRadius: 2,
                padding: 0,
                boxShadow: 0,
              }}
            >
              {tab.panel}
            </TabPanel>
          ))}
      </TabContext>
    </Box>
  );
}

export default TicketTab;
